
import { GeoTIFFImage } from "geotiff";
import {getGDALStats} from "./get-gdal-stats";
import {calcBandStats} from "./calc-band-stats";

type bandStats = {
  min?: number;
  max?: number;
  mean?: number;
  median?: number;
  mode?: number;
  stddev?: number;
}
type Stats = {
 bands: bandStats[]
}

type Options = {
  debug?: boolean;
  enough?: string[];
  calcStatsOptions?: {[key: string]: any};
}
/**
 * Get the stats for a GeoTIFF Image
 * 
 * @param  image - the image object from tiff.getImage() using the geotiff.js library
 * @param  options
 * @param  options.debug - set to true to log more information
 * @param  options.enough - if these stats are in the GDAL Metadata, then we will avoid running calcStats on all the images' pixels. default is ["min", "max"]. valid values are "min", "max", "median", "mean", "sum", "mode", "modes"
 * @param  options.calcStatsOptions - options to pass along to [calc-stats](https://github.com/danieljdufour/calc-stats#advanced-usage)
 * @param  selectedBands - Band numbers to be read from (where the first band is `1`). If not provided, all bands will
 * be read.
 * @returns  - Array of Statistics Objects
 */
export async function getStats(image: GeoTIFFImage, options: Options, selectedBands?: number[]) {
  const { debug, enough = ["min", "max"] } = options;
  if (debug) console.log("[geotiff-stats] debug:", debug);

  const calcStatsOptions = typeof options.calcStatsOptions === "object" ? { ...options.calcStatsOptions } : {};

  const noDataValue = image.getGDALNoData();
  if (debug) console.log("[geotiff-stats] noDataValue:", noDataValue);

  const samplesPerPixel = image.getSamplesPerPixel();
  if (debug) console.log("[geotiff-stats] samplesPerPixel:", samplesPerPixel);

  const stats: Stats = {
    bands: []
  };
  const bands = selectedBands && selectedBands.length ? selectedBands.map(x => x - 1) : Array.from(Array(samplesPerPixel).keys());
  const bandCount = bands.length

  for (let i = 0; i < bandCount; i++) {
    const bandIndex = bands[i];
    if (debug) console.log("[geotiff-stats] bandIndex:", bandIndex);
    const bandStats = getGDALStats(image, bandIndex);

    const isFloat = image.getSampleFormat(bandIndex) === 3;

    const sufficient = enough.every(key => typeof bandStats[key] === "number");
    if (debug) console.log("[geotiff-stats] sufficient:", sufficient);

    if (!sufficient) {
      if (debug)
        console.log(
          "[geotiff-stats] we weren't able to parse enough stats from the image's metadata, so running calcStats"
        );

      // let rangeFilter;
      // if (typeof bandStats.max === "number" && typeof bandStats.min === "number") {
      //   rangeFilter = ({ value }) => value >= bandStats.min && value <= bandStats.max;
      // } else if (typeof bandStats.max === "number") {
      //   rangeFilter = ({ value }) => value <= bandStats.max;
      // } else if (typeof bandStats.min === "number") {
      //   rangeFilter = ({ value }) => value >= bandStats.min;
      // }

      let signFilter: (({value}: {value: number}) => boolean) | undefined = undefined;
      if (isFloat && typeof bandStats.min === "number" && bandStats.min >= 0) {
        // sometimes there's an inconsistency in the GDAL NoData value for 32-bit floating point rasters
        // and I'm not sure why, but we can fix it
        // if all the values are positive (according to the GDAL Metadata), except for the No Data Value,
        // then if we see a negative number, it's probably going to supposed to be no data
        signFilter = ({ value }) => value >= 0;
      }

      if (typeof signFilter === "function") {
        if (typeof calcStatsOptions.filter === "function") {
          const userProvidedFilter = calcStatsOptions.filter;
          calcStatsOptions.filter = (data: any) => {
            return signFilter!(data) && userProvidedFilter(data);
          };
        } else {
          calcStatsOptions.filter = signFilter;
        }
      }
      const calculated = await calcBandStats({ image, bandIndex, calcStatsOptions });
      Object.assign(bandStats, calculated);
    }

    stats.bands.push(bandStats);
  }
  if (debug) console.log("[geotiff-stats] returning: " + JSON.stringify(stats));
  return stats;
}
